import { api } from '../../../lib/api';
import { assetService } from '../../../services/activos/assetService';
export const warehouseService = {
  /**
   * List all work orders with filtering and pagination
   * @param {Object} filters - Filter criteria (factory_name, status, asset_id, etc.)
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Number of items per page
   * @returns {Promise<Object>} Response with data array and pagination info
   */
  async list(filters = {}, page = 1, pageSize = 10) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('page_size', pageSize);

      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const url = `/api/maintenance/work-orders?${queryParams.toString()}`;
      const data = await api.get(url);
      return data || { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching work orders:', error);
      throw error;
    }
  },

  async getWarehouses() {
    try {
      const warehouses = await assetService.getAssetsByProduct(309);
      return warehouses || { data: [] };
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      throw error;
    }
  },

  async updateWarehouse(warehouseId, updateData) {
    try {
      const data = await api.put(
        `/api/asset/update/${warehouseId}`,
        updateData
      );
      return data;
    } catch (error) {
      console.error(`Error updating work order ${warehouseId}:`, error);
      throw error;
    }
  },

  /**
   * Get a specific work order by ID
   * @param {number} workOrderId - The work order ID
   * @returns {Promise<Object>} The work order with tasks and assignments
   */
  async getById(workOrderId) {
    try {
      const data = await api.get(
        `/api/maintenance/work-orders/${workOrderId}`
      );
      return data || null;
    } catch (error) {
      console.error(`Error fetching work order ${workOrderId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new work order
   * @param {Object} workOrderData - Work order data (title, asset_id, work_order_type, priority, scheduled_date, estimated_hours, etc.)
   * @returns {Promise<Object>} The created work order
   */
  async create(workOrderData) {
    try {
      const data = await api.post('/api/maintenance/work-orders', workOrderData);
      return data;
    } catch (error) {
      console.error('Error creating work order:', error);
      throw error;
    }
  },




  /**
   * Delete a work order
   * @param {number} workOrderId - The work order ID
   * @returns {Promise<void>}
   */
  async delete(workOrderId) {
    try {
      await api.delete(`/api/maintenance/work-orders/${workOrderId}`);
    } catch (error) {
      console.error(`Error deleting work order ${workOrderId}:`, error);
      throw error;
    }
  },

  /**
   * Start a work order
   * @param {number} workOrderId - The work order ID
   * @returns {Promise<void>}
   */
  async start(workOrderId) {
    try {
      await api.post(`/api/maintenance/work-orders/start/${workOrderId}`, {});
    } catch (error) {
      console.error(`Error starting work order ${workOrderId}:`, error);
      throw error;
    }
  },

  /**
   * Complete a work order
   * @param {number} workOrderId - The work order ID
   * @param {Object} completeData - Completion data (completion_notes, quality_check)
   * @returns {Promise<void>}
   */
  async complete(workOrderId, completeData = {}) {
    try {
      await api.post(
        `/api/maintenance/work-orders/complete/${workOrderId}`,
        completeData
      );
    } catch (error) {
      console.error(`Error completing work order ${workOrderId}:`, error);
      throw error;
    }
  },

  // ========== TASK METHODS ==========

  /**
   * Add a task to a work order
   * @param {number} workOrderId - The work order ID
   * @param {Object} taskData - Task data (task_number, description, assigned_to, status)
   * @returns {Promise<Object>} The created task
   */
  async addTask(workOrderId, taskData) {
    try {
      const data = await api.post(
        `/api/maintenance/work-orders/tasks/${workOrderId}`,
        taskData
      );
      return data;
    } catch (error) {
      console.error(
        `Error adding task to work order ${workOrderId}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Update a task
   * @param {number} taskId - The task ID
   * @param {Object} updateData - Updated task data (description, assigned_to, status)
   * @returns {Promise<Object>} The updated task
   */
  async updateTask(taskId, updateData) {
    try {
      const data = await api.put(
        `/api/maintenance/work-orders/tasks/${taskId}`,
        updateData
      );
      return data;
    } catch (error) {
      console.error(`Error updating task ${taskId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a task
   * @param {number} taskId - The task ID
   * @returns {Promise<void>}
   */
  async deleteTask(taskId) {
    try {
      await api.delete(`/api/maintenance/work-orders/tasks/${taskId}`);
    } catch (error) {
      console.error(`Error deleting task ${taskId}:`, error);
      throw error;
    }
  },

  // ========== ASSIGNMENT METHODS ==========

  /**
   * Add an assignment to a work order
   * @param {number} workOrderId - The work order ID
   * @param {Object} assignmentData - Assignment data (employee_id, role, start_time, end_time, hours_worked)
   * @returns {Promise<Object>} The created assignment
   */
  async addAssig(workOrderId, assignmentData) {
    try {
      const data = await api.post(
        `/api/maintenance/work-orders/assignments/${workOrderId}`,
        assignmentData
      );
      return data;
    } catch (error) {
      console.error(
        `Error adding assignment to work order ${workOrderId}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Update an assignment
   * @param {number} assignmentId - The assignment ID
   * @param {Object} updateData - Updated assignment data
   * @returns {Promise<Object>} The updated assignment
   */
  async updateAssig(assignmentId, updateData) {
    try {
      const data = await api.put(
        `/api/maintenance/work-orders/assignments/${assignmentId}`,
        updateData
      );
      return data;
    } catch (error) {
      console.error(`Error updating assignment ${assignmentId}:`, error);
      throw error;
    }
  },

  /**
   * Remove an assignment from a work order
   * @param {number} assignmentId - The assignment ID
   * @returns {Promise<void>}
   */
  async deleteAssig(assignmentId) {
    try {
      await api.delete(
        `/api/maintenance/work-orders/assignments/${assignmentId}`
      );
    } catch (error) {
      console.error(`Error removing assignment ${assignmentId}:`, error);
      throw error;
    }
  },

  /**
   * Add an external assignment to a work order
   * @param {number} workOrderId - The work order ID
   * @param {Object} assignmentData - Assignment data (employee_id, role, start_time, end_time, hours_worked)
   * @returns {Promise<Object>} The created assignment
   */
  async addExtAssig(workOrderId, assignmentData) {
    try {
      const data = await api.post(
        `/api/maintenance/external-companies/work-orders-assignments/${workOrderId}`,
        assignmentData
      );
      return data;
    } catch (error) {
      console.error(
        `Error adding external assignment to work order ${workOrderId}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Update an external assignment
   * @param {number} assignmentId - The assignment ID
   * @param {Object} updateData - Updated assignment data
   * @returns {Promise<Object>} The updated assignment
   */
  async updateExtAssig(assignmentId, updateData) {
    try {
      const data = await api.put(
        `/api/maintenance/external-companies/work-orders-assignments/${assignmentId}`,
        updateData
      );
      return data;
    } catch (error) {
      console.error(`Error updating external assignment ${assignmentId}:`, error);
      throw error;
    }
  },

  /**
   * Remove an external assignment from a work order
   * @param {number} assignmentId - The assignment ID
   * @returns {Promise<void>}
   */
  async deleteExtAssig(assignmentId) {
    try {
      await api.delete(
        `/api/maintenance/external-companies/work-orders-assignments/${assignmentId}`
      );
    } catch (error) {
      console.error(`Error removing external assignment ${assignmentId}:`, error);
      throw error;
    }
  },
};
